import type { DocumentNode } from 'graphql';
import type {
	AddToCache,
	UpdateItemsInCache,
	RemoveFromCache,
} from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import type {
	BaseIssueNode,
	BaseListData,
} from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { GenericVariables, GenericGroupVariables } from '../../../common/types';
import { getParentIssueId, isGroupItem } from '../../../common/utils/hierarchy';
import type {
	UpdateGroupItemInCache,
	AddGroupItemToCache,
	BaseGroupItemNode,
} from '../../item-groups-cache';

export interface CreateViewUpdaterCallbackOptions<
	TIssueNode extends BaseIssueNode,
	_TListData extends BaseListData<TIssueNode>,
> {
	query: DocumentNode;
	addToCache: AddToCache;
	updateItemsInCache: UpdateItemsInCache;
	removeFromCache: RemoveFromCache;
	isHierarchyEnabled: () => boolean;
	isInfiniteHierarchyEnabled: () => boolean; // We cannot use FF, as other view might have it disabled.
	createVariablesForItemWithoutParent: () => GenericVariables;
	createVariablesForItemWithParent: (parentIssueId: string) => GenericVariables;
	shouldChildItemBeAddedToOrRemovedFromCache: (parentIssueId: string) => boolean;
	onUpdateParentWithNewChild: (parentItem: TIssueNode, childItem: TIssueNode) => TIssueNode;
	getParentOfIssueIfExist?: (issueId: string) => undefined | string;
	isItemHierarchySupported?: (item: TIssueNode) => boolean;
	removeChildFromParent?: (existingItem: TIssueNode, totalItems: number) => TIssueNode;
	updateIssueParentMap?: (issueId: string, newParentId?: string) => void;
	getPrevParentOfIssueIfExists?: (issueId: string) => string | undefined;
	createVariablesForGroupItem?: (groupIssueId: string) => GenericVariables;
	isGroupingByStatus?: boolean;
	isGrouping?: boolean;
}

/**
 * This util is used to combine both `useViewUpdater` with `useCacheActions`
 * The util will generate callbacks for `useViewUpdater` by using functions from `useCacheActions`
 * The reason for this util is to avoid logic duplication between the consumers (e.g. list view and timeline view)
 */
export const createViewUpdaterCallbacksWithCacheActions = <
	TIssueNode extends BaseIssueNode,
	TListData extends BaseListData<TIssueNode>,
>({
	isHierarchyEnabled,
	isInfiniteHierarchyEnabled,
	query,
	addToCache,
	removeFromCache,
	updateItemsInCache,
	createVariablesForItemWithParent,
	createVariablesForItemWithoutParent,
	shouldChildItemBeAddedToOrRemovedFromCache,
	onUpdateParentWithNewChild,
	getParentOfIssueIfExist,
	isItemHierarchySupported,
	removeChildFromParent,
	updateIssueParentMap,
	getPrevParentOfIssueIfExists,
	createVariablesForGroupItem,
	isGroupingByStatus = false,
	isGrouping = false,
}: CreateViewUpdaterCallbackOptions<TIssueNode, TListData>): {
	addToCacheCallback: (items: TIssueNode[]) => void;
	removeFromCacheCallback: (items: TIssueNode[]) => void;
} => {
	const getParentVariables = (parentId?: string) => {
		const variables = createVariablesForItemWithoutParent();
		if (!parentId) {
			return variables;
		}
		if (createVariablesForGroupItem && isGroupItem(parentId)) {
			return createVariablesForGroupItem(parentId);
		}
		return createVariablesForItemWithParent(parentId);
	};

	const updateIssueParent = ({
		prevParentIssueId,
		parentIssueId,
		item,
	}: {
		prevParentIssueId?: string;
		parentIssueId?: string;
		item: TIssueNode;
	}) => {
		updateIssueParentMap && updateIssueParentMap(item.issueId, parentIssueId);
		if (
			(!prevParentIssueId && !isGrouping) ||
			(isGrouping && prevParentIssueId) ||
			(prevParentIssueId && shouldChildItemBeAddedToOrRemovedFromCache(prevParentIssueId))
		) {
			removeFromCache({
				query,
				variables: getParentVariables(prevParentIssueId),
				issueId: Number(item.issueId),
			});
		}

		if (isGroupingByStatus) {
			return;
		}
		const hasParentToRemove = getParentOfIssueIfExist && !!getParentOfIssueIfExist(item.issueId);
		if (removeChildFromParent && prevParentIssueId && hasParentToRemove) {
			const grandParentId = getParentOfIssueIfExist
				? getParentOfIssueIfExist(String(prevParentIssueId))
				: undefined;

			if (isGroupItem(grandParentId) || isGroupItem(prevParentIssueId)) {
				return;
			}

			updateItemsInCache<TIssueNode, TListData>({
				query,
				variables: getParentVariables(grandParentId),
				issueIds: [String(prevParentIssueId)],
				updateItem: (existingParentIssue) => removeChildFromParent(existingParentIssue, 1),
			});
		}

		if (!parentIssueId) {
			return;
		}

		// updateChildCount of new parent
		// so user will have an indicator that shows the parent has subtask now
		const newGrandParentId = getParentOfIssueIfExist
			? getParentOfIssueIfExist(parentIssueId.toString())
			: undefined;

		if (isGroupItem(newGrandParentId) || isGroupItem(parentIssueId)) {
			return;
		}

		updateItemsInCache<TIssueNode, TListData>({
			query,
			variables: getParentVariables(newGrandParentId),
			issueIds: [parentIssueId],
			updateItem: (existingIssue) => {
				const updatedIssueWithNewChild = onUpdateParentWithNewChild(existingIssue, item);
				return updatedIssueWithNewChild;
			},
		});
	};

	const addToCacheCallback = (items: TIssueNode[]) => {
		if (isHierarchyEnabled()) {
			const itemsWithoutParent = items.filter((i) => getParentIssueId(i) === undefined);
			const itemsWithParent = items.filter((i) => getParentIssueId(i) !== undefined);
			// add items WITHOUT parent first
			if (itemsWithoutParent.length > 0) {
				addToCache<TIssueNode, TListData>({
					newNodes: itemsWithoutParent,
					query,
					variables: createVariablesForItemWithoutParent(),
				});
				items.forEach((item) => {
					// when parent is unlinked from issue
					const parentIssueId = undefined;
					const prevParentIssueId = getPrevParentOfIssueIfExists
						? getPrevParentOfIssueIfExists(item.issueId)
						: undefined;
					if (isInfiniteHierarchyEnabled() && prevParentIssueId !== parentIssueId) {
						updateIssueParent({ prevParentIssueId, parentIssueId, item });
					}
				});
			}

			// then add items WITH parent
			itemsWithParent.forEach((item) => {
				const parentIssueId = getParentIssueId(item);

				if (parentIssueId === undefined) {
					// this scenario should be impossible because we already filtered the item
					// but doing this just to remove the Flow error
					return;
				}

				// CLEAN UP THE FOLLOWING CONDITION WITH as it's only used by timeline view - followed by https://jbusiness.atlassian.net/browse/HRZ-1391
				if (isItemHierarchySupported && !isItemHierarchySupported(item)) {
					// this is a specific scenario for the TIMELINE
					// in that view we don't support epic hierarchy (we don't show the epic children, those are displayed on the same level)
					// in that case we should not use the parent variables because that query doesn't exist
					addToCache<TIssueNode, TListData>({
						newNodes: [item],
						query,
						variables: createVariablesForItemWithoutParent(),
					});
					return;
				}

				const prevParentIssueId = getPrevParentOfIssueIfExists
					? getPrevParentOfIssueIfExists(item.issueId)
					: undefined;

				const shouldUpdateIssueParent =
					isInfiniteHierarchyEnabled() && prevParentIssueId !== parentIssueId;

				const shouldChildItemBeAddedOrRemovedFromCache =
					shouldChildItemBeAddedToOrRemovedFromCache(parentIssueId);

				if (shouldUpdateIssueParent) {
					updateIssueParent({ prevParentIssueId, parentIssueId, item });
				}

				// We only want to add item if the existing query for children-of-this-parent exists
				// this is to prevent apollo error where the cache key is missing
				if (shouldChildItemBeAddedOrRemovedFromCache) {
					addToCache<TIssueNode, TListData>({
						newNodes: [item],
						query,
						variables: createVariablesForItemWithParent(parentIssueId),
					});
				} else if (!shouldChildItemBeAddedOrRemovedFromCache && !shouldUpdateIssueParent) {
					let variables = createVariablesForItemWithoutParent();
					if (
						isInfiniteHierarchyEnabled() && // Grandparent only exists in infinite hierarchy
						getParentOfIssueIfExist
					) {
						const grandParentId = getParentOfIssueIfExist(parentIssueId.toString());
						if (grandParentId != null) {
							variables = createVariablesForItemWithParent(grandParentId);
						}
					}

					// if the parent is not listed to have child yet
					// we will update the parent to show children
					// so user will have an indicator that shows the parent has subtask now
					updateItemsInCache<TIssueNode, TListData>({
						query,
						variables,
						issueIds: [parentIssueId],
						updateItem: (existingIssue) => {
							const updatedIssueWithNewChild = onUpdateParentWithNewChild(existingIssue, item);
							return updatedIssueWithNewChild;
						},
					});
				}
			});

			return;
		}

		addToCache<TIssueNode, TListData>({
			newNodes: items,
			query,
			variables: createVariablesForItemWithoutParent(),
		});
	};

	const removeFromCacheCallback = (items: TIssueNode[]) => {
		items.forEach((item) => {
			if (isHierarchyEnabled()) {
				const parentIssueId = getParentIssueId(item);

				if (parentIssueId !== undefined) {
					if (shouldChildItemBeAddedToOrRemovedFromCache(parentIssueId)) {
						removeFromCache<TIssueNode, TListData>({
							issueId: item.issueId,
							query,
							variables: createVariablesForItemWithParent(parentIssueId),
						});
					}

					// TODO remove the subtask from parent 'Subtask' field https://jbusiness.atlassian.net/browse/JET-1883

					return;
				}
			}

			removeFromCache<TIssueNode, TListData>({
				issueId: item.issueId,
				query,
				variables: createVariablesForItemWithoutParent(),
			});
		});
	};

	return {
		addToCacheCallback,
		removeFromCacheCallback,
	};
};

export type CreateGroupingViewUpdaterCacheActionsProps<
	TIssueNode extends BaseIssueNode,
	_TListData extends BaseListData<TIssueNode>,
> = {
	query: DocumentNode;
	groupsQuery: DocumentNode;
	addToCache: AddToCache;
	removeFromCache: RemoveFromCache;
	addGroupItemToCache: AddGroupItemToCache;
	updateGroupItemInCache: UpdateGroupItemInCache;
	updateItemsInCache: UpdateItemsInCache;
	createVariablesForGroupItem: (groupIssueId: string) => GenericVariables;
	createVariablesForGroups: () => GenericGroupVariables;
	createItemGroup: (item: TIssueNode) => BaseGroupItemNode | undefined;
	findItemGroup: (item: TIssueNode) => BaseIssueNode | undefined;
	findGroupById: (groupIssueId: string) => BaseIssueNode | undefined;
	canUpdateGroupCache: (groupIssueId: string) => boolean;
	getParentOfIssueIfExist: (issueId: string) => undefined | string;
	getPrevParentOfIssueIfExists: (issueId: string) => string | undefined;
	onUpdateParentWithNewChild: (parentItem: TIssueNode, childItem: TIssueNode) => TIssueNode;
	removeChildFromParent?: (existingItem: TIssueNode, totalItems: number) => TIssueNode;
	createVariablesForItemWithParent: (parentIssueId: string) => GenericVariables;
	updateIssueParentMap: (issueId: string, newParentId?: string) => void;
};

export const createGroupingViewUpdaterCacheActions = <
	TIssueNode extends BaseIssueNode,
	TGroupData extends BaseListData<TIssueNode>,
>({
	query,
	groupsQuery,
	addToCache,
	removeFromCache,
	addGroupItemToCache,
	updateGroupItemInCache,
	updateItemsInCache,
	createVariablesForGroups,
	createVariablesForGroupItem,
	createItemGroup,
	findItemGroup,
	findGroupById,
	canUpdateGroupCache,
	getParentOfIssueIfExist,
	getPrevParentOfIssueIfExists,
	onUpdateParentWithNewChild,
	removeChildFromParent,
	createVariablesForItemWithParent,
	updateIssueParentMap,
}: CreateGroupingViewUpdaterCacheActionsProps<TIssueNode, TGroupData>): {
	addToCache: (
		items: TIssueNode[],
		options?: {
			updateCountOnly: boolean;
		},
	) => void;
	removeFromCache: (items: TIssueNode[]) => void;
} => {
	const getReparentingInfo = (item: TIssueNode) => {
		const currentParent = getParentIssueId(item);
		const prevParent = getPrevParentOfIssueIfExists(String(item.issueId));
		// note that currentParent can be undefined or a groupitem if parent is unlinked
		const isUnlinkParent =
			(!currentParent || isGroupItem(currentParent)) && prevParent && currentParent !== prevParent;
		const isReparenting = currentParent && currentParent !== prevParent;
		return {
			isReparenting,
			isUnlinkParent,
			currentParent,
			prevParent,
		};
	};

	const removeItemFromCache = (item: TIssueNode, currentGroupIssueId: string) => {
		const { isReparenting, prevParent, isUnlinkParent } = getReparentingInfo(item);
		// check if items data exists in apollo cache, so that updating it won't blow up apollo
		if (
			canUpdateGroupCache(currentGroupIssueId) &&
			((!isReparenting && !isUnlinkParent) || (prevParent && isGroupItem(prevParent)))
		) {
			removeFromCache<TIssueNode, TGroupData>({
				issueId: item.issueId,
				query,
				variables: createVariablesForGroupItem(currentGroupIssueId),
			});
		} else if (prevParent && (isReparenting || isUnlinkParent) && !isGroupItem(prevParent)) {
			removeFromCache<TIssueNode, TGroupData>({
				issueId: item.issueId,
				query,
				variables: createVariablesForItemWithParent(prevParent),
			});
		}

		const groupItem = findGroupById(currentGroupIssueId);
		const prevGrandParent = prevParent ? getParentOfIssueIfExist(prevParent) : undefined;
		if (groupItem) {
			if (
				prevParent &&
				(isReparenting || isUnlinkParent) &&
				prevGrandParent &&
				removeChildFromParent
			) {
				// update count of prevParent
				updateItemsInCache<TIssueNode, TGroupData>({
					query,
					variables: isGroupItem(prevGrandParent)
						? createVariablesForGroupItem(prevGrandParent)
						: createVariablesForItemWithParent(prevGrandParent),
					issueIds: [prevParent],
					updateItem: (existingParentIssue) => removeChildFromParent(existingParentIssue, 1),
				});
			} else if (isChildIssueInGroup(groupItem, item.issueId)) {
				// update totalGroupItems in itemGroups cache.
				updateGroupItemInCache({
					query: groupsQuery,
					variables: createVariablesForGroups(),
					id: groupItem.id,
					updateItem: (group) => ({
						...group,
						totalCount:
							group.totalCount !== undefined ? Math.max(0, group.totalCount - 1) : undefined,
					}),
				});
			}
		}
	};

	const getGroupIssueId = (issueId: string): null | string => {
		if (isGroupItem(issueId)) {
			return issueId;
		}
		const parentId = getParentOfIssueIfExist ? getParentOfIssueIfExist(issueId) : undefined;

		if (!parentId) {
			return null;
		}
		if (isGroupItem(parentId)) {
			return parentId;
		}
		return getGroupIssueId(parentId);
	};

	const isChildIssueInGroup = (group: BaseIssueNode, childIssueId: string) =>
		group.children?.some((issue) => issue.issueId === childIssueId);

	const addToGroupCache = (
		items: TIssueNode[],
		{
			updateCountOnly = false,
		}: {
			updateCountOnly: boolean;
		} = { updateCountOnly: false },
	) => {
		items.forEach((item) => {
			const { isReparenting, currentParent, prevParent } = getReparentingInfo(item);
			const currentGroupIssueId = prevParent ? getGroupIssueId(prevParent) : undefined;
			const nextGroup = findItemGroup(item);

			if (currentGroupIssueId != null && !updateCountOnly) {
				removeItemFromCache(item, currentGroupIssueId);
			}

			if (nextGroup != null) {
				// check if items data exists in apollo cache, so that updating it won't blow up apollo
				const shouldAddToCache =
					canUpdateGroupCache(nextGroup.issueId) &&
					!updateCountOnly &&
					(fg('list-view-issue-disappear-upon-editing-fix') || !isReparenting);
				if (shouldAddToCache) {
					// add the item to the next group
					addToCache<TIssueNode, TGroupData>({
						newNodes: [item],
						query,
						variables: createVariablesForGroupItem(nextGroup.issueId),
					});
					// this should not be necessary once background refetch when grouping is working correctly
					updateIssueParentMap(item.issueId);
				}

				const newGrandParentId = currentParent ? getParentOfIssueIfExist(currentParent) : undefined;

				if (currentParent && isReparenting && newGrandParentId) {
					updateItemsInCache<TIssueNode, TGroupData>({
						query,
						variables: isGroupItem(newGrandParentId)
							? createVariablesForGroupItem(newGrandParentId)
							: createVariablesForItemWithParent(newGrandParentId),
						issueIds: [currentParent],
						updateItem: (existingIssue) => {
							const updatedIssueWithNewChild = onUpdateParentWithNewChild(existingIssue, item);
							return updatedIssueWithNewChild;
						},
					});
				} else {
					// update totalGroupItems in itemGroups cache.
					updateGroupItemInCache({
						query: groupsQuery,
						variables: createVariablesForGroups(),
						id: nextGroup.id,
						updateItem: (group) => ({
							...group,
							totalCount:
								group.totalCount !== undefined ? Math.max(0, group.totalCount + 1) : undefined,
						}),
					});
				}
			}
			// create a new group
			else {
				const newGroup = createItemGroup(item);
				if (newGroup != null) {
					addGroupItemToCache({
						query: groupsQuery,
						variables: createVariablesForGroups(),
						newNodes: [newGroup],
					});
				}
			}
		});
	};

	const removeFromGroupCache = (items: TIssueNode[]) => {
		items.forEach((item) => {
			const prevParent = getPrevParentOfIssueIfExists(String(item.issueId));
			const currentGroupIssueId = prevParent ? getGroupIssueId(prevParent) : undefined;
			// happens when updating parent but new parent is not expanded
			if (currentGroupIssueId != null) {
				removeItemFromCache(item, currentGroupIssueId);
			}
		});
	};

	return { addToCache: addToGroupCache, removeFromCache: removeFromGroupCache };
};
